import React, { useEffect, useRef } from 'react';
import type { ChoiceType, GameMode } from '../hooks/useGameLogic';

interface ResultModalProps {
  isOpen: boolean;
  type: ChoiceType;
  content: string;
  playerName: string;
  mode: GameMode;
  onClose: () => void;
  badgeLabel?: string;
}

const modeStyles: Record<GameMode, { bg: string; accent: string; glow: string }> = {
  soft: {
    bg: 'from-blue-800 to-blue-950',
    accent: 'from-green-400 to-emerald-500',
    glow: 'shadow-[0_0_40px_rgba(74,222,128,0.3)]',
  },
  hard: {
    bg: 'from-violet-900 to-purple-950',
    accent: 'from-violet-500 to-blue-500',
    glow: 'shadow-[0_0_40px_rgba(124,58,237,0.3)]',
  },
  roulette: {
    bg: 'from-green-900 to-green-950',
    accent: 'from-green-400 to-emerald-500',
    glow: 'shadow-[0_0_40px_rgba(74,222,128,0.3)]',
  },
  coquin: {
    bg: 'from-red-900 to-red-950',
    accent: 'from-red-500 to-pink-500',
    glow: 'shadow-[0_0_40px_rgba(239,68,68,0.3)]',
  },
};

export default function ResultModal({ isOpen, type, content, playerName, mode, onClose, badgeLabel }: ResultModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const styles = modeStyles[mode];

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isCoquin = mode === 'coquin';
  const fontClass = isCoquin ? 'font-cursive' : 'font-display';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`relative w-full max-w-sm rounded-3xl bg-gradient-to-b ${styles.bg} p-6 ${styles.glow} animate-slide-up`}
      >
        {/* Type badge */}
        <div className="flex justify-center mb-4">
          <span
            className={`inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r ${styles.accent} text-white font-display font-bold text-sm uppercase tracking-wider`}
          >
            {badgeLabel || (type === 'question' ? 'Question ❓' : 'Action ⚡')}
          </span>
        </div>

        {/* Player name */}
        <p className="text-center text-white/60 font-body text-sm mb-2">
          C'est le tour de
        </p>
        <h3 className={`text-center ${fontClass} font-bold text-2xl text-white mb-6`}>
          {playerName}
        </h3>

        {/* Content */}
        <div className="glass rounded-2xl p-5 mb-6 min-h-[100px] flex items-center justify-center">
          <p className={`text-center ${fontClass} text-lg text-white leading-relaxed`}>
            {content}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className={`w-full py-3.5 rounded-2xl bg-gradient-to-r ${styles.accent} text-white font-display font-bold text-base btn-press`}
        >
          Compris ! 👍
        </button>
      </div>
    </div>
  );
}
